const PickerProvider = require('./PictProvider-InputType-Picker.js');

/** @type {Record<string, any>} */
const _DEFAULT_CONFIGURATION =
{
	ProviderIdentifier: 'SampleTypePicker',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
	AutoSolveWithApp: false,
};

/**
 * Sample-type picker (beast B1) — the Picker-stack port of the legacy SampleTypeSelector (a
 * MaterialsEntitySelector subclass; this class mirrors it 1:1 for diff-review). Reuses the base broker
 * (Entity=SampleType via the specialized-selector map) and keeps the sample's two type columns in sync
 * through the cascade seam: the modern `IDSampleType` FK (set by the standard autofill) and the legacy
 * `MaterialType` code. On forward selection it mirrors the picked type's `Hash` onto `MaterialType`;
 * when seeding from a linked sample it prefers `IDSampleType` and falls back to resolving the
 * `MaterialType` code to a SampleType so pre-backfill samples still display (and pick up the FK on the
 * next save). Declarative defaults (`Default:"ACPT"` + `DefaultField:"Hash"`) come from the base broker
 * via the map's MaterialsFamily flag.
 */
class PictProviderInputTypeSampleTypePicker extends PickerProvider
{
	/**
	 * @param {Object} pFable @param {Object} [pOptions] @param {string} [pServiceHash]
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DEFAULT_CONFIGURATION, pOptions), pServiceHash);
		/** @type {Record<string, any>|null} Hash -> SampleType record, lazily loaded for the reverse fallback. */
		this._sampleTypesByHash = null;
		/** @type {boolean} true while the map request is in flight; kept separate from the map so an unloaded map stays null. */
		this._sampleTypeMapLoading = false;
		/** @type {Array<{View: Object, Input: Object, RowIndex: number|string|null}>} inputs that initialized mid-load and owe a seed. */
		this._sampleTypeMapWaiters = [];
		/** @type {boolean} re-entrancy guard so a fallback-driven commit does not re-seed mid-seed. */
		this._fallbackSeeding = false;
	}

	/** @param {Object} pInput @return {string} marshal-relative address of the sample's legacy MaterialType code. */
	_sampleMaterialTypeAddress(pInput)
	{
		return pInput?.PictForm?.SampleMaterialTypeAddress || 'SampleID__Record.MaterialType';
	}
	/** @param {Object} pInput @return {string} marshal-relative address of the sample's IDSampleType FK. */
	_sampleIDSampleTypeAddress(pInput)
	{
		return pInput?.PictForm?.SampleIDSampleTypeAddress || 'SampleID__Record.IDSampleType';
	}

	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		const tmpResult = super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		this._loadSampleTypeMap(pView, pInput, null);
		// The legacy seed fired via the init-time record refresh; the broker has no init-time cascade, so
		// attempt it directly — its own gates (sample already carries the FK / no legacy code) make it a no-op
		// everywhere it should not run.
		this._seedSampleTypeFromMaterialType(pView, pInput, null);
		return tmpResult;
	}

	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		const tmpResult = super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		this._loadSampleTypeMap(pView, pInput, pRowIndex);
		this._seedSampleTypeFromMaterialType(pView, pInput, pRowIndex);
		return tmpResult;
	}

	/**
	 * Legacy re-ran the seed on every marshal (MES _onDataMarshalToForm → _onRecordRefresh), which is what
	 * caught a linked sample record landing after init. The seed's own gates keep it a no-op otherwise.
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		const tmpResult = super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		this._seedSampleTypeFromMaterialType(pView, pInput, null);
		return tmpResult;
	}

	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		// @ts-ignore — inherited through the PSP chain tsc cannot see.
		const tmpResult = super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		this._seedSampleTypeFromMaterialType(pView, pInput, pRowIndex);
		return tmpResult;
	}

	/**
	 * Lazily cache Hash -> SampleType for the current customer (row-level auth scopes the read).
	 *
	 * The load is async and the caller seeds immediately after calling this, so EVERY input that initializes
	 * while the request is in flight is queued and re-seeded when it lands — not just the first. The map
	 * itself stays `null` until it is real (the in-flight state is tracked separately), so the emptiness
	 * guard in `_seedSampleTypeFromMaterialType` means what it says: a seed during the window is suppressed
	 * rather than resolving against an empty map and silently giving up. The waiter carries its row index so
	 * a tabular picker replays against the row it initialized on.
	 *
	 * @param {Object} [pView] @param {Object} [pInput] the picker to re-seed once the map loads.
	 * @param {number|string|null} [pRowIndex] row the picker initialized on, for the tabular case.
	 */
	_loadSampleTypeMap(pView, pInput, pRowIndex)
	{
		if (this._sampleTypesByHash || !this.pict?.HeadlightRestClient)
		{
			return;
		}
		// The provider family is commonly instantiated via Object.create in harnesses and by the broker, so
		// do not assume the constructor ran.
		if (!Array.isArray(this._sampleTypeMapWaiters))
		{
			this._sampleTypeMapWaiters = [];
		}
		if (pView && pInput)
		{
			this._sampleTypeMapWaiters.push({ View: pView, Input: pInput, RowIndex: (pRowIndex === undefined) ? null : pRowIndex });
		}
		if (this._sampleTypeMapLoading)
		{
			return;
		}
		this._sampleTypeMapLoading = true;
		this.pict.HeadlightRestClient.getJSON('SampleTypes/FilteredTo/FSF~IDSampleType~ASC~0/0/500', (pError, pTypes) =>
		{
			this._sampleTypeMapLoading = false;
			const tmpWaiters = this._sampleTypeMapWaiters;
			this._sampleTypeMapWaiters = [];
			if (pError || !Array.isArray(pTypes))
			{
				// Reset to null (not an empty map) so the next onInputInitialize retries rather than being
				// stuck resolving against a map that will never fill.
				this._sampleTypesByHash = null;
				return;
			}
			const tmpMap = {};
			for (const tmpType of pTypes)
			{
				if (tmpType && tmpType.Hash)
				{
					tmpMap[tmpType.Hash] = tmpType;
				}
			}
			this._sampleTypesByHash = tmpMap;
			for (const tmpWaiter of tmpWaiters)
			{
				this._seedSampleTypeFromMaterialType(tmpWaiter.View, tmpWaiter.Input, tmpWaiter.RowIndex);
			}
		});
	}

	/**
	 * The cascade twin of the legacy `_onRecordRefresh`: fired after `__Record` lands (select and clear).
	 * @param {Object} pView @param {Record<string, any>} pInput
	 * @param {Record<string, any>|Array<Record<string, any>>} pRecord
	 * @param {number|string} [pRowIndex] @param {string} [pTransactionGUID]
	 */
	_onEntityRecordCascade(pView, pInput, pRecord, pRowIndex, pTransactionGUID)
	{
		super._onEntityRecordCascade(pView, pInput, pRecord, pRowIndex, pTransactionGUID);
		if (pRecord && typeof pRecord === 'object' && !Array.isArray(pRecord) && pRecord.IDSampleType)
		{
			// Forward: mirror the picked type's Hash onto the sample's legacy MaterialType column.
			this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), this._sampleMaterialTypeAddress(pInput), pRecord.Hash || '');
			return;
		}
		this._seedSampleTypeFromMaterialType(pView, pInput, pRowIndex);
	}

	/**
	 * Reverse fallback: when nothing is selected and the bound sample has no IDSampleType but carries a
	 * legacy MaterialType code, resolve it to a SampleType, seed the picker, and backfill the FK in-form.
	 * @param {Object} pView @param {Object} pInput @param {number|string|null} [pRowIndex]
	 */
	_seedSampleTypeFromMaterialType(pView, pInput, pRowIndex)
	{
		if (this._fallbackSeeding || !this._sampleTypesByHash || !pView || typeof pView.getMarshalDestinationObject !== 'function')
		{
			return;
		}
		const tmpDestination = pView.getMarshalDestinationObject();
		// Only seed an EMPTY picker (found live: this seam fires from init and the async map-load too,
		// where a value may already be committed). The legacy provider reached the same outcome
		// structurally — its seed was only ever entered from a record refresh whose record had no
		// IDSampleType, and a valued picker's init refresh takes the forward-mirror branch instead —
		// so without this gate a stale legacy code would hijack a real selection on reload.
		const tmpValueAddress = this._getRecordAddress(pInput, pRowIndex).replace(/__Record$/, '');
		if (this.pict.manifest.getValueAtAddress(tmpDestination, tmpValueAddress))
		{
			return;
		}
		const tmpExistingID = this.pict.manifest.getValueAtAddress(tmpDestination, this._sampleIDSampleTypeAddress(pInput));
		if (tmpExistingID)
		{
			return;
		}
		const tmpMaterialType = this.pict.manifest.getValueAtAddress(tmpDestination, this._sampleMaterialTypeAddress(pInput));
		const tmpResolved = tmpMaterialType ? this._sampleTypesByHash[tmpMaterialType] : null;
		if (!tmpResolved)
		{
			return;
		}
		this._fallbackSeeding = true;
		try
		{
			// Backfill the FK + pre-seed __Record synchronously from the map (offline-tolerant: the commit's
			// record refresh re-resolves the same id and, on a failed lookup, keeps this pre-seeded record),
			// then land the picker value through the value-commit primitive — model write + fenced provider
			// loop (record refresh → cascade, whose forward branch re-mirrors MaterialType idempotently) +
			// widget repaint.
			this.pict.manifest.setValueAtAddress(tmpDestination, this._sampleIDSampleTypeAddress(pInput), tmpResolved.IDSampleType);
			this.pict.manifest.setValueAtAddress(tmpDestination, this._getRecordAddress(pInput, pRowIndex), tmpResolved);
			this._setValueAndCommit(pView, pInput, tmpResolved.IDSampleType, pRowIndex);
		}
		finally
		{
			this._fallbackSeeding = false;
		}
	}
}

module.exports = PictProviderInputTypeSampleTypePicker;

module.exports.default_configuration = _DEFAULT_CONFIGURATION;
